package com.partejar.app.feature.relacionamentos.presentation

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.partejar.app.feature.relacionamentos.data.*
import com.partejar.app.feature.contratos.data.ContratosDataSource
import com.partejar.app.feature.contratos.data.ContractSetupRequirements
import com.partejar.app.feature.contratos.data.setupRequirements
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch
import kotlinx.coroutines.CoroutineScope
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime

enum class RelationshipDetailAction { INTERACTION, MEETING, UPDATE, STAGE }

class RelacionamentoDetailViewModel private constructor(private val source: RelacionamentosDataSource, private val testScope: CoroutineScope?, private val contracts: ContratosDataSource?, @Suppress("UNUSED_PARAMETER") testable: Unit) : ViewModel() {
    constructor(source: RelacionamentosDataSource, contracts: ContratosDataSource? = null) : this(source, null, contracts, Unit)
    internal constructor(source: RelacionamentosDataSource, scope: CoroutineScope) : this(source, scope, null, Unit)
    internal constructor(source: RelacionamentosDataSource, contracts: ContratosDataSource, scope: CoroutineScope) : this(source, scope, contracts, Unit)
    private val scope get() = testScope ?: viewModelScope
    private val _detail = MutableStateFlow<RelationshipDetail?>(null); val detail = _detail.asStateFlow()
    private val _error = MutableStateFlow<String?>(null); val error = _error.asStateFlow()
    private val _busyAction = MutableStateFlow<RelationshipDetailAction?>(null); val busyAction = _busyAction.asStateFlow()
    private val _completedAction = MutableSharedFlow<RelationshipDetailAction>(extraBufferCapacity = 1); val completedAction = _completedAction.asSharedFlow()
    private val _setupRequired = MutableStateFlow<ContractSetupRequirements?>(null)
    val setupRequired = _setupRequired.asStateFlow()
    fun dismissSetupRequired() { _setupRequired.value = null }
    private var loadJob: Job? = null
    private var requestedId: Long? = null
    fun load(id: Long): Job {
        requestedId = id
        loadJob?.cancel()
        return scope.launch {
            if (_busyAction.value != null && _detail.value?.id == id) return@launch
            if (_detail.value?.id != id) _detail.value = null
            _error.value = null
            try {
                val loaded = source.detail(id)
                currentCoroutineContext().ensureActive()
                _detail.value = loaded
            } catch (error: CancellationException) {
                throw error
            } catch (_: Exception) {
                currentCoroutineContext().ensureActive()
                _error.value = "Não foi possível carregar o contato."
            }
        }.also { loadJob = it }
    }
    fun interact(id: Long, kind: InteractionKind, note: String, next: String?) = scope.launch {
        execute(id, RelationshipDetailAction.INTERACTION, "Não foi possível registrar o contato.") {
            source.interact(id, kind, note.ifBlank { null }, next)
        }
    }
    fun interact(id: Long, note: String, next: String?) = interact(id, InteractionKind.FOLLOW_UP, note, next)
    fun scheduleMeeting(id: Long, startsAt: String, note: String?) = scope.launch {
        val endsAt = LocalDateTime.parse(startsAt).toInstant(TimeZone.currentSystemDefault())
            .plus(1, DateTimeUnit.HOUR).toLocalDateTime(TimeZone.currentSystemDefault()).toString()
        execute(id, RelationshipDetailAction.MEETING, "Não foi possível agendar a reunião.") {
            source.scheduleMeeting(id, startsAt, endsAt, note?.ifBlank { null })
        }
    }
    fun move(id: Long, stage: RelacionamentoStage) = scope.launch {
        if (_busyAction.value != null) return@launch
        if (requestedId != null && requestedId != id) return@launch
        loadJob?.cancel()
        if (stage == RelacionamentoStage.CONTRACT) {
            _busyAction.value = RelationshipDetailAction.STAGE
            _error.value = null
            try {
                val requirements = requireNotNull(contracts).options().setupRequirements()
                currentCoroutineContext().ensureActive()
                if (requestedId != null && requestedId != id) return@launch
                if (requirements.isMissing) {
                    _setupRequired.value = requirements
                    return@launch
                }
            } catch (error: CancellationException) { throw error }
            catch (_: Exception) {
                _error.value = "Não foi possível verificar os planos e modelos de contrato. Tente novamente."
                return@launch
            } finally { _busyAction.value = null }
        }
        execute(id, RelationshipDetailAction.STAGE, "Não foi possível atualizar a etapa.") { source.move(id, stage) }
    }
    fun updateDueDate(id: Long, dueDate: String) = scope.launch {
        val current = _detail.value ?: return@launch
        if (runCatching { LocalDate.parse(dueDate) }.isFailure) {
            _error.value = "Informe uma DPP válida no formato AAAA-MM-DD."
            return@launch
        }
        execute(id, RelationshipDetailAction.UPDATE, "Não foi possível salvar a DPP.") {
            source.updateDueDate(id, current, dueDate)
        }
    }
    fun update(id: Long, edit: RelationshipEdit) = scope.launch {
        val current = _detail.value ?: return@launch
        if (edit.name.isBlank()) {
            _error.value = "Informe o nome da gestante."
            return@launch
        }
        if (edit.dueDate != null && runCatching { LocalDate.parse(edit.dueDate) }.isFailure) {
            _error.value = "Informe uma DPP válida no formato AAAA-MM-DD."
            return@launch
        }
        execute(id, RelationshipDetailAction.UPDATE, "Não foi possível salvar as alterações.") {
            source.update(id, current, edit)
        }
    }

    fun dismissError() { _error.value = null }

    private suspend fun execute(id: Long, action: RelationshipDetailAction, failureMessage: String, operation: suspend () -> RelationshipDetail) {
        if (_busyAction.value != null) return
        if (requestedId != null && requestedId != id) return
        loadJob?.cancel()
        _busyAction.value = action
        _error.value = null
        try {
            val updated = operation()
            currentCoroutineContext().ensureActive()
            if (requestedId != null && requestedId != id) return
            _detail.value = updated
            _completedAction.tryEmit(action)
        } catch (error: CancellationException) {
            throw error
        } catch (_: Exception) {
            currentCoroutineContext().ensureActive()
            if (requestedId == null || requestedId == id) _error.value = failureMessage
        } finally {
            _busyAction.value = null
        }
    }
}
